import { appServiceApi } from "../../../app/appServiceApi";
import { API_URL } from "../../../constants/apiUrl";
import { GetQueryArgsType } from "../../../types";
import {
  CheckSalaryDisbursementType,
  CheckSalaryDisbursementTypeParamsType,
  CheckVendorDisbursementType,
  CheckVendorDisbursementTypeParamsType,
  GetAllVendorBatchDataParamsType,
  GetAllVendorBatchDataType,
  GetAllVendorDataParamsType,
  GetAllVendorDataType,
  GetBalanceCheckDataType,
  GetSingleVendorDataType,
  GetVendorTotalAmountDataType,
  OtpRequestTimeDataType,
} from "../../../types/portal/vendorPayment";

export const vendorPaymentApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllVendorBatch: builder.query<
      GetAllVendorBatchDataType,
      GetQueryArgsType<GetAllVendorBatchDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllVendorBatch,
        params: queryArgs.params,
      }),
      providesTags: ["GetAllVendorBatch"],
    }),

    createVendor: builder.mutation({
      query: (data) => ({
        url: API_URL.createVendor,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetVendorPayment",
            "GetBalanceCheck",
            "GetAllVendorBatch",
            "GetVendorAmount",
          ];

        return [];
      },
    }),

    getAllVendor: builder.query<
      GetAllVendorDataType,
      GetQueryArgsType<GetAllVendorDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllVendor,
        params: queryArgs.params,
      }),
      providesTags: ["GetVendorPayment"],
    }),

    getSingleVendor: builder.query<GetSingleVendorDataType, string>({
      query: (id) => ({
        url: `${API_URL.getSingleVendor}/${id}`,
      }),
    }),

    vendorPaymentChecker: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.vendorPaymentChecker}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetVendorPayment",
            "GetBalanceCheck",
            "GetAllVendorBatch",
            "GetVendorAmount",
          ];

        return [];
      },
    }),

    vendorPayment: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.vendorPayment}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetVendorPayment",
            "GetBalanceCheck",
            "GetAllVendorBatch",
            "GetVendorAmount",
          ];

        return [];
      },
    }),

    balanceCheck: builder.query<
      GetBalanceCheckDataType,
      { walletNumber: string; companyId: number }
    >({
      query: ({ walletNumber, companyId }) => {
        return {
          url: `${API_URL.balanceCheck}/${walletNumber}/${companyId}`,
        };
      },
      providesTags: ["GetBalanceCheck"],
    }),
    sendOtp: builder.mutation({
      query: (queryArgs) => ({
        url: API_URL.otpRequest,
        method: "POST",
        ...queryArgs,
      }),
      invalidatesTags: (result) => {
        if (result) return ["OtpRequestTime"];

        return [];
      },
    }),
    verifyOtp: builder.mutation({
      query: (data) => ({
        url: API_URL.verifyOtp,
        method: "POST",
        body: data,
      }),
    }),
    verifyPin: builder.mutation({
      query: ({ data, headers }) => ({
        url: API_URL.verifyPin,
        method: "POST",
        body: data,
        headers,
      }),
    }),
    vendorPaymentWithOtp: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.vendorPaymentWithOtp}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetVendorPayment",
            "GetBalanceCheck",
            "GetAllVendorBatch",
            "GetVendorAmount",
            "CheckVendorDisbursement",
            "CheckSalaryDisbursement",
          ];

        return [];
      },
    }),
    getVendorAmount: builder.query<
      GetVendorTotalAmountDataType,
      GetQueryArgsType<GetAllVendorDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.vendorAmount,
        params: queryArgs.params,
      }),
      providesTags: ["GetVendorAmount"],
    }),
    checkVendorDisbursement: builder.query<
      CheckVendorDisbursementType,
      GetQueryArgsType<CheckVendorDisbursementTypeParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.checkVendorDisbursement,
        params: queryArgs.params,
      }),
      providesTags: ["CheckVendorDisbursement"],
    }),
    checkSalaryDisbursement: builder.query<
      CheckSalaryDisbursementType,
      GetQueryArgsType<CheckSalaryDisbursementTypeParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.checkSalaryDisbursement,
        params: queryArgs.params,
      }),
      providesTags: ["CheckSalaryDisbursement"],
    }),

    otpRequestTime: builder.query<OtpRequestTimeDataType, string>({
      query: (token) => ({
        url: `${API_URL.otpRequestTime}`,
        headers: {
          Authorization: `Bearer ${token}`,
          "ngrok-skip-browser-warning": "true",
        },
        providesTags: ["OtpRequestTime"],
      }),
    }),

    reSendOtp: builder.mutation({
      query: (queryArgs) => ({
        url: API_URL.otpRequest,
        method: "POST",
        ...queryArgs,
      }),
      invalidatesTags: (result) => {
        if (result) return ["OtpRequestTime"];

        return [];
      },
    }),
  }),
});

export const {
  useCreateVendorMutation,
  useGetAllVendorQuery,
  useGetSingleVendorQuery,
  useVendorPaymentCheckerMutation,
  useVendorPaymentMutation,
  useBalanceCheckQuery,
  useGetAllVendorBatchQuery,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useVendorPaymentWithOtpMutation,
  useGetVendorAmountQuery,
  useVerifyPinMutation,
  useCheckVendorDisbursementQuery,
  useCheckSalaryDisbursementQuery,
  useOtpRequestTimeQuery,
  useReSendOtpMutation,
} = vendorPaymentApi;
